"use client";
import * as React from "react";
import { PhoneInputProps } from "@/lib/Types";
import clsx from "clsx";

const SCROLLBAR_STYLES = `
  .dropdown-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(150,150,150,0.4) transparent;
  }
  .dropdown-scroll::-webkit-scrollbar {
    width: 4px;
  }
  .dropdown-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .dropdown-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(150,150,150,0.4);
    border-radius: 9999px;
  }
  .dropdown-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(150,150,150,0.7);
  }
`;

export const PhoneInput: React.FC<PhoneInputProps> = ({
  variant = "light",
  selectedCountry,
  countryListArray = [],
  setSelectedCountry,
  setFormData,
  phone,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const ref = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const isDark = variant === "dark";

  const filteredCountries = countryListArray.filter((country: any) =>
    [
      country.countryName ?? "",
      country.slug ?? "",
      country.code ?? "",
    ]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div
      className={clsx(
        "flex items-center gap-0 border-b border-solid min-h-[59px] w-full",
        isDark
          ? "border-white/20 text-white"
          : "border-b-[color:var(--Stroke-2,#E3E3E3)] text-zinc-600"
      )}
    >
      <style>{SCROLLBAR_STYLES}</style>

      {/* Country Code Picker */}
      <div ref={ref} className="relative shrink-0">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`Selected country: ${selectedCountry.slug} ${selectedCountry.code}. Click to change`}
          onClick={() => {
            setIsOpen((prev) => {
              if (!prev) {
                setSearchTerm("");
              }
              return !prev;
            });
          }}
          className={clsx(
            "flex items-center gap-2 bg-transparent outline-none font-semibold tracking-normal pr-3 text-base",
            isDark ? "text-white" : "text-zinc-600"
          )}
        >
          <span>{`${selectedCountry.slug} ${selectedCountry.code}`}</span>

          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={clsx(
              "shrink-0 transition-transform",
              isOpen && "rotate-180",
              isDark ? "text-white" : "text-zinc-500"
            )}
            aria-hidden
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Searchable Dropdown */}
        {isOpen && (
          <div
            className={clsx(
              "absolute top-full left-0 z-20 w-64 mt-1 rounded-md overflow-hidden",
              isDark
                ? "bg-[#1a1f2e] border border-white/20 backdrop-blur-md"
                : "bg-white border border-gray-100 shadow-lg"
            )}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200/20">
              <input
                ref={searchRef}
                type="text"
                placeholder="Search country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className={clsx(
                  "w-full px-3 py-2 text-sm rounded-md outline-none",
                  isDark
                    ? "bg-white/10 text-white placeholder:text-white/60"
                    : "bg-gray-50 text-zinc-700 placeholder:text-zinc-500"
                )}
              />
            </div>

            {/* Country List */}
            <ul
              role="listbox"
              className="dropdown-scroll max-h-52 overflow-y-auto"
            >
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country: any, idx: number) => (
                  <li
                    key={`${country.code}-${idx}`}
                    role="option"
                    aria-selected={
                      country.slug === selectedCountry.slug &&
                      country.code === selectedCountry.code
                    }
                    onClick={() => {
                      setSelectedCountry(country);

                      setFormData((prev: any) => ({
                        ...prev,
                        country: country.countryName,
                        countryCode: country.code,
                      }));

                      setSearchTerm("");
                      setIsOpen(false);
                    }}
                    className={clsx(
                      "px-4 py-2 cursor-pointer text-sm transition-colors",
                      isDark ? "hover:bg-white/10" : "hover:bg-gray-100",
                      country.slug === selectedCountry.slug &&
                        country.code === selectedCountry.code &&
                        (isDark
                          ? "bg-white/15 font-medium"
                          : "bg-gray-50 font-medium")
                    )}
                  >
                    <div className="flex flex-col">
                      <span>{`${country.slug} ${country.code}`}</span>

                      {country.countryName && (
                        <span
                          className={clsx(
                            "text-xs",
                            isDark ? "text-white/60" : "text-zinc-500"
                          )}
                        >
                          {country.countryName}
                        </span>
                      )}
                    </div>
                  </li>
                ))
              ) : (
                <li
                  className={clsx(
                    "px-4 py-3 text-sm",
                    isDark ? "text-white/60" : "text-zinc-500"
                  )}
                >
                  No countries found
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Phone Number Input */}
      <input
        type="tel"
        placeholder="Phone Number"
        className={clsx(
          "flex-1 bg-transparent outline-none text-base pl-3",
          isDark
            ? "text-white placeholder-white/50"
            : "text-zinc-600 placeholder-zinc-500"
        )}
        aria-label="Phone Number"
        value={phone}
        onChange={(e) =>
          setFormData((prev: any) => ({
            ...prev,
            phone: e.target.value,
          }))
        }
      />
    </div>
  );
};